import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import { toastMessage } from "../../constants/strings";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import {
  OldRoute,
  OldRoutesSchema,
  Route,
  routesSchema,
  routeV3ArraySchema,
  UpdateRoute,
} from "../../types/route.type";
import { http } from "../../utils/httpCommon";
import { toast } from "../../utils/snackbarHelper";
import { useApiUrls } from "../useApiUrl";
import { shipmentTagSchema } from "./useInboundUrgentAndCriticalCare";

export type NewRoute = Omit<Route, "driverId" | "doorId" | "equipmentType"> & {
  driverId: number | null;
  doorId: number;
};

const errors = {
  presetRouteAlreadyExists: "PRESET_ROUTE_NAME_ALREADY_EXISTS",
};

export const newPresetRoute: NewRoute = {
  id: 0,
  name: "",
  zones: [],
  zoneIds: [],
  zipCodes: [],
  startTime: "",
  endPoint: "",
  endTime: "",
  trailerId: null,
  tractorId: null,
  straightTruckId: null,
  driverId: null,
  doorId: 0,
  isActive: true,
  hereFeatureId: "",
  sicId: 0,
  isRoute: true,
};

const useRoutesKeys = {
  routes: (serviceCenterId: number) => ["routes", serviceCenterId],
  routesV3: (serviceCenterId: number) => ["routesV3", serviceCenterId],
  route: (serviceCenterId: number, routeId: number) => [
    "routes",
    serviceCenterId,
    routeId,
  ],
  validateZipCode: (serviceCenterId: number, zipCode: string) => [
    "routes",
    serviceCenterId,
    "zipcodes",
    zipCode,
  ],
};

const getRoute = async (
  sicId: number,
  routeId: number,
  getRoutesAndTrapsV2Url: string
) => {
  const { data } = await http.get(getRoutesAndTrapsV2Url, {
    params: { sicId },
  });

  const routes = routesSchema.parse(data);
  return routes.find((route) => route.id === routeId) || null;
};

export const useRoutes = (serviceCenterId: number, enabled = true) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  const getRoutes = async (sicId: number) => {
    const { data } = await http.get(urls.getRoutesAndTraps, {
      params: { sicId, isRoute: true },
    });
    return OldRoutesSchema.parse(data);
  };

  return useQuery({
    enabled,
    queryKey: useRoutesKeys.routes(serviceCenterId),
    queryFn: () => getRoutes(serviceCenterId),
  });
};

export const useRoutesV3 = (serviceCenterId: number, enabled = true) => {
  const urls = useApiUrls();

  return useQuery({
    enabled,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- urls is stable during session
    queryKey: useRoutesKeys.routesV3(serviceCenterId),
    queryFn: async () => {
      const { data } = await http.get(urls.getRoutesV3, {
        params: { sicId: serviceCenterId, isRoute: true, "only-active": true },
      });
      return routeV3ArraySchema.parse(data);
    },
  });
};

export const useRoutesAndTraps = (serviceCenterId: number, enabled = true) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const isCacheEnabled = useIsFeatureFlagEnabled(
    "operations-5890-settings-routes-and-traps-pagination-client"
  );

  const getRoutesAndTraps = async (sicId: number) => {
    const { data } = await http.get(urls.getRoutesAndTrapsV2, {
      params: { sicId },
    });
    return routesSchema.parse(data);
  };

  return useQuery({
    enabled,
    queryKey: ["routes", serviceCenterId],
    queryFn: () => getRoutesAndTraps(serviceCenterId),
    staleTime: isCacheEnabled ? Infinity : 0,
  });
};

export const useRoute = (serviceCenterId: number, routeId: number) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const isCacheEnabled = useIsFeatureFlagEnabled(
    "operations-5890-settings-routes-and-traps-pagination-client"
  );

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: useRoutesKeys.route(serviceCenterId, routeId),
    queryFn: () => getRoute(serviceCenterId, routeId, urls.getRoutesAndTrapsV2),
    staleTime: isCacheEnabled ? Infinity : 0,
  });
};

export type PresetRouteType = "Route" | "Trap";

export const useCreatePresetRoutes = (
  onSuccess: () => void,
  type: PresetRouteType,
  sicId: number
) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (route: NewRoute) => {
      const url = urls.postRoutes;
      const { data } = await http.post<OldRoute[]>(url, [
        {
          ...route,
          driverId: route.driverId || 0,
          tractorId: route.isRoute ? route.tractorId : null,
          endTime: route.endTime || null,
          doorId: route.doorId || 0,
          name: route.name,
        },
      ]);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useRoutesKeys.routes(sicId),
      });
      onSuccess();
    },
    onError: (error) => {
      if (
        axios.isAxiosError(error) &&
        error.response?.data === errors.presetRouteAlreadyExists
      ) {
        return toast(
          type === "Route"
            ? toastMessage.settings.createRouteTrap.routeNameAlreadyExists
            : toastMessage.settings.createRouteTrap.trapNameAlreadyExists,
          {
            variant: "error",
          }
        );
      }
      toast(toastMessage.settings.createRouteTrap.failed, {
        variant: "error",
      });
    },
  });
};

export const useEditPresetRoute = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (route: Route) => {
      const updateRouteReq: UpdateRoute = {
        ...route,
        tractorId: route.isRoute ? route.tractorId : null,
        driverId: route.driverId || 0,
        doorId: route.doorId || 0,
      };
      // TODO: Replace with GET API https://dylt.atlassian.net/browse/IODD-2477
      await http.put(urls.putRoutes(route.id), updateRouteReq);
      return route;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: useRoutesKeys.routes(data.sicId),
      });
      queryClient.invalidateQueries({
        queryKey: useRoutesKeys.route(data.sicId, data.id),
      });
      onSuccess();
    },
    onError: () =>
      toast(toastMessage.settings.editRouteTrap.failed, {
        variant: "error",
      }),
  });
};

export type DeleteRouteCallbackArgs = {
  routeName: string;
  routeType: "Route" | "Trap";
};

type UseDeleteRouteArgs = {
  sicId: number;
  onSuccess?: (args: DeleteRouteCallbackArgs) => void;
  onError?: (args: DeleteRouteCallbackArgs) => void;
};

export const useDeleteRoute = ({
  sicId,
  onSuccess,
  onError,
}: UseDeleteRouteArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (route: {
      id: number;
      name: string;
      isRoute: boolean;
    }) => {
      const { status } = await http.delete(urls.deleteRoute(route.id));
      return status;
    },
    onSuccess: (_data, route) => {
      onSuccess?.({
        routeName: route.name,
        routeType: route.isRoute ? "Route" : "Trap",
      });
    },
    onError: (_error, route) => {
      onError?.({
        routeName: route.name,
        routeType: route.isRoute ? "Route" : "Trap",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          const routesPrefix = useRoutesKeys.routes(sicId);
          return (
            Array.isArray(key) &&
            key.length >= routesPrefix.length &&
            routesPrefix.every((segment, i) => key[i] === segment)
          );
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["routes", "zipcodes", "geolocation"],
      });
    },
  });
};

const validateZipCodeSchema = z.object({
  zipCode: z.string(),
  isValid: z.boolean(),
});

const validateZipCodesSchema = validateZipCodeSchema.array().nonempty();
type ValidatedZipCodesResponse = z.infer<typeof validateZipCodesSchema>;

type UseValidateZipCodeArgs = {
  serviceCenterId: number;
  zipCode: string;
};

export const useValidateZipCode = ({
  serviceCenterId,
  zipCode,
}: UseValidateZipCodeArgs) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: useRoutesKeys.validateZipCode(serviceCenterId, zipCode),
    queryFn: async (): Promise<ValidatedZipCodesResponse> => {
      const { data } = await http.post(urls.validateZipCodes, [zipCode]);
      return validateZipCodesSchema.parse(data);
    },
    enabled: !!zipCode,
    select: (data) => data[0],
  });
};

const moveZipCodeSchema = z.object({
  zipCodeId: z.number(),
  sourceRouteId: z.number(),
  destinationRouteId: z.number(),
  shipmentTagIds: z.array(z.number()).optional(),
});

export type MoveZipCodesRequest = z.infer<typeof moveZipCodeSchema>;

type UseMoveZipCodesArgs = {
  sicId: number;
  onSuccess?: (response: MoveZipCodesRequest) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
};

export const useMoveZipCodes = ({
  sicId,
  onSuccess,
  onError,
  onSettled,
}: UseMoveZipCodesArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (request: MoveZipCodesRequest) => {
      await http.put(urls.moveZipCodesV2, request);

      return request;
    },
    onSuccess: (request) => {
      onSuccess?.(request);
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: useRoutesKeys.routes(sicId),
      });
      onSettled?.();
    },
  });
};

const createZipCodeRequestSchema = z.object({
  sicId: z.number(),
  presetRouteId: z.number(),
  zipCode: z.string(),
  shipmentTagIds: z.array(z.number()),
});

const createZipCodeResponseSchema = z.object({
  zipCodeId: z.number(),
  zipCode: z.string(),
  shipmentTags: z.array(shipmentTagSchema),
});

export type CreateZipCodePayload = z.infer<typeof createZipCodeRequestSchema>;
export type CreateZipCodeResponse = z.infer<typeof createZipCodeResponseSchema>;

interface UseCreateZipCodeArgs {
  sicId: number;
  onSuccess?: (data: CreateZipCodeResponse) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export const useCreateZipCodeWithShipmentTags = ({
  sicId,
  onSuccess,
  onError,
  onSettled,
}: UseCreateZipCodeArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (payload: CreateZipCodePayload) => {
      createZipCodeRequestSchema.parse(payload);

      const response = await http.post(urls.createZipCode, payload);

      return createZipCodeResponseSchema.parse(response.data);
    },
    onSuccess: (data: CreateZipCodeResponse) => {
      queryClient.invalidateQueries({ queryKey: ["routes", sicId] });
      onSuccess?.(data);
    },
    onError: (error, _payload, _context) => {
      onError?.(error);
    },
    onSettled: () => {
      onSettled?.();
    },
  });
};

const editZipCodeRequestSchema = z.object({
  sicId: z.number(),
  shipmentTagIds: z.array(z.number()),
});

const editZipCodeResponseSchema = z.object({
  zipCodeId: z.number(),
  zipCode: z.string(),
  shipmentTags: z.array(shipmentTagSchema),
});

type EditZipCodePayload = z.infer<typeof editZipCodeRequestSchema>;
type EditZipCodeResponse = z.infer<typeof editZipCodeResponseSchema>;

interface UseEditZipCodeArgs {
  sicId: number;
  zipCodeId: number;
  onSuccess?: (data: EditZipCodeResponse) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export function useEditZipCodeWithShipmentTags({
  sicId,
  zipCodeId,
  onSuccess,
  onError,
  onSettled,
}: UseEditZipCodeArgs) {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (payload: EditZipCodePayload) => {
      editZipCodeRequestSchema.parse(payload);

      const { data } = await http.put(
        urls.updateAndDeleteZipcode(zipCodeId),
        payload
      );

      return editZipCodeResponseSchema.parse(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["routes", sicId],
      });
      onSuccess?.(data);
    },
    onError: (err) => {
      onError?.(err);
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}

const deleteZipCodeRequestSchema = z.object({
  sicId: z.number(),
});

type DeleteZipCodePayload = z.infer<typeof deleteZipCodeRequestSchema> & {
  zipCodeId: number;
};
interface UseDeleteZipCodeArgs {
  sicId: number;
  zipCodeId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export function useDeleteZipCode({
  zipCodeId,
  sicId,
  onSuccess,
  onError,
  onSettled,
}: UseDeleteZipCodeArgs) {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useMutation({
    mutationFn: async (payload: DeleteZipCodePayload) => {
      const { sicId } = deleteZipCodeRequestSchema.parse({
        sicId: payload.sicId,
      });

      await http.delete(urls.updateAndDeleteZipcode(zipCodeId), {
        data: { sicId },
      });

      return zipCodeId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes", sicId] });
      onSuccess?.();
    },
    onError: (err) => {
      onError?.(err);
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}

const availableShipmentTagsSchema = z.object({
  zipCodeId: z.number().nullable(),
  zipCode: z.string(),
  sicId: z.number(),
  usedTags: z.array(shipmentTagSchema),
  unusedTags: z.array(shipmentTagSchema),
});

type AvailableShipmentTags = z.infer<typeof availableShipmentTagsSchema>;

interface UseAvailableShipmentTagsArgs {
  zipCode: string;
  sicId: number;
  zipCodeId?: number;
}

export const useAvailableShipmentTags = ({
  zipCode,
  sicId,
  zipCodeId,
}: UseAvailableShipmentTagsArgs) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;

  return useQuery<AvailableShipmentTags>({
    queryKey: ["availableTags", sicId, zipCodeId, zipCode],
    queryFn: async () => {
      const { data } = await http.get(urls.getAvailableTags(zipCode), {
        params: { sicId, zipCodeId },
      });
      return availableShipmentTagsSchema.parse(data);
    },
  });
};
